/*───────────────────────────────────────────────────────────────
 📄 File: components/ui/RecordingUploadModeSection.tsx
 🧩 Description:
    Upload mode section for RecordingTtsModal.
    Renders a drag-and-drop zone or selected-file preview
    with play / replace / remove controls.
────────────────────────────────────────────────────────────────*/

import { FileAudio, Play, Upload, X } from 'lucide-react';
import { Button } from './button';
import type { VoiceRecording } from '../../modules/recordingManagement/types';

interface FileUploadHook {
    selectedFile: File | null;
    filePreview: string | null;
    dragActive: boolean;
    fileInputRef: React.RefObject<HTMLInputElement>;
    handleDrag: (e: React.DragEvent) => void;
    handleDrop: (e: React.DragEvent) => void;
    handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    removeFile: () => void;
}

interface Props {
    isEditMode: boolean;
    existingRecording: VoiceRecording | null;
    currentAudioUrl?: string;
    fileUpload: FileUploadHook;
    onPlay: () => void;
}

export function RecordingUploadModeSection({ isEditMode, existingRecording, currentAudioUrl, fileUpload, onPlay }: Props) {
    const showDropZone =
        !fileUpload.selectedFile &&
        !(isEditMode && existingRecording && !existingRecording.is_ai_generated);

    if (showDropZone) {
        return (
            <div className="space-y-3">
                <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${fileUpload.dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                    onDragEnter={fileUpload.handleDrag} onDragLeave={fileUpload.handleDrag}
                    onDragOver={fileUpload.handleDrag} onDrop={fileUpload.handleDrop}
                    onClick={() => fileUpload.fileInputRef.current?.click()}
                >
                    <FileAudio className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-medium mb-1">Drag &amp; drop or click to select</p>
                    <p className="text-xs text-muted-foreground">WAV, MP3, OGG, FLAC — max 50 MB</p>
                    <input ref={fileUpload.fileInputRef} type="file" accept="audio/*" onChange={fileUpload.handleFileInputChange} className="hidden" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3 border rounded-lg p-3 bg-muted/40">
                <FileAudio className="h-8 w-8 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                        {fileUpload.selectedFile ? fileUpload.selectedFile.name : existingRecording?.filename || 'Existing file'}
                    </p>
                    {fileUpload.selectedFile && (
                        <p className="text-xs text-muted-foreground">{(fileUpload.selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {currentAudioUrl && (
                        <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-green-600 hover:bg-green-500/10" onClick={onPlay}>
                            <Play className="h-4 w-4" />
                        </Button>
                    )}
                    {isEditMode && !fileUpload.selectedFile && (
                        <Button type="button" variant="outline" size="sm" onClick={() => fileUpload.fileInputRef.current?.click()}>
                            <Upload className="h-4 w-4 mr-1" /> Replace
                        </Button>
                    )}
                    {fileUpload.selectedFile && (
                        <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={fileUpload.removeFile}>
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                </div>
                <input ref={fileUpload.fileInputRef} type="file" accept="audio/*" onChange={fileUpload.handleFileInputChange} className="hidden" />
            </div>
        </div>
    );
}
